import React, { useState, useEffect } from 'react';
import { Save, Database, Link, X, Plus } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';
import { useNotification } from '../context/NotificationContext';
import { usePermission } from '../context/PermissionContext';

const apiFetch = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(url, { ...options, headers });
  if (res.status === 401) { localStorage.removeItem('token'); window.location.href = '/login'; throw new Error('Session expired'); }
  return res.json();
};

const FORMULA_MODE = '__formula__';

const SystemAttributeMappingPage = () => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const { hasPermission } = usePermission();
  const canSave = hasPermission('channel_system_attribute_mapping', 'edit');

  const [stores, setStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [systemAttributes, setSystemAttributes] = useState([]);
  const [storeAttributes, setStoreAttributes] = useState([]);
  const [mappings, setMappings] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [checkedRows, setCheckedRows] = useState({});

  /**
   * mappings[storeAttrCode] = {
   *   erp_attribute_code: '' | systemAttrCode | '__formula__',
   *   default_value: string,
   *   formula: string (only when erp_attribute_code === '__formula__')
   * }
   */

  // Load stores
  useEffect(() => {
    apiFetch('/api/channel/stores').then(res => {
      if (res.status === 'success') {
        const s = res.data.stores || [];
        setStores(s);
        if (s.length === 1) setSelectedStoreId(String(s[0].auth_id));
      }
    }).catch(() => {});
  }, []);

  // Load mapping data when store changes
  useEffect(() => {
    if (!selectedStoreId) return;
    setLoading(true);
    apiFetch(`/api/channel/system-attribute-mapping?store_id=${selectedStoreId}`)
      .then(res => {
        if (res.status === 'success') {
          const sysAttrs = res.data.system_attributes || [];
          const stAttrs = res.data.store_attributes || [];
          const loadedMappings = res.data.mappings || {};

          setSystemAttributes(sysAttrs);
          setStoreAttributes(stAttrs);

          // Convert loaded data to local state
          const next = {};
          for (const chCode of stAttrs.map(a => a.attribute_code)) {
            const loaded = loadedMappings[chCode];
            if (loaded) {
              const entry = {
                erp_attribute_code: loaded.erp_attribute_code || '',
                default_value: loaded.default_value || '',
                formula: '',
              };
              if (loaded.transform_rule && loaded.transform_rule.formula) {
                entry.erp_attribute_code = FORMULA_MODE;
                entry.formula = loaded.transform_rule.formula;
              }
              next[chCode] = entry;
            }
            // else: not mapped, not included in state (will be handled on save)
          }
          setMappings(next);
          // 全部默认不勾选，勾选后才可编辑保存
          setCheckedRows({});
        }
      })
      .catch(() => notify('Failed to load mapping data', 'error'))
      .finally(() => setLoading(false));
  }, [selectedStoreId, notify]);

  const getMapping = (chCode) => mappings[chCode] || { erp_attribute_code: '', default_value: '', formula: '' };

  const handleSourceChange = (chCode, value) => {
    setMappings(prev => ({
      ...prev,
      [chCode]: { ...getMapping(chCode), erp_attribute_code: value },
    }));
  };

  const handleDefaultValueChange = (chCode, value) => {
    setMappings(prev => ({
      ...prev,
      [chCode]: { ...getMapping(chCode), default_value: value },
    }));
  };

  const handleFormulaChange = (chCode, value) => {
    setMappings(prev => ({
      ...prev,
      [chCode]: { ...getMapping(chCode), formula: value },
    }));
  };

  const toggleCheck = (chCode) => {
    setCheckedRows(prev => ({ ...prev, [chCode]: !prev[chCode] }));
  };

  const clearMapping = (chCode) => {
    setMappings(prev => ({
      ...prev,
      [chCode]: { erp_attribute_code: '', default_value: '', formula: '' },
    }));
  };

  const handleSave = async () => {
    if (!selectedStoreId) return;
    setSaving(true);
    try {
      // Convert local mappings to API format — 只传勾选的行
      const mappingArray = [];
      for (const [chCode, entry] of Object.entries(mappings)) {
        if (!checkedRows[chCode]) continue; // 未勾选不保存

        const item = { channel_attribute_code: chCode, default_value: entry.default_value || null };

        if (entry.erp_attribute_code === FORMULA_MODE) {
          item.erp_attribute_code = null;
          item.transform_rule = entry.formula ? { formula: entry.formula } : null;
        } else {
          // 始终发送 erp_attribute_code（空则传 null），允许在服务端清空映射
          item.erp_attribute_code = entry.erp_attribute_code || null;
        }

        mappingArray.push(item);
      }

      const res = await apiFetch('/api/channel/system-attribute-mapping/save', {
        method: 'POST',
        body: JSON.stringify({ store_id: parseInt(selectedStoreId), mappings: mappingArray }),
      });

      if (res.status === 'success') {
        notify('Mappings saved successfully', 'success');
      } else {
        notify(res.message || 'Save failed', 'error');
      }
    } catch (err) {
      notify('Save error: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const filteredStoreAttrs = storeAttributes.filter(a => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (a.attribute_code || '').toLowerCase().includes(q)
      || (a.default_label || '').toLowerCase().includes(q);
  });

  const selectedStore = stores.find(s => String(s.auth_id) === selectedStoreId);

  const getDefaultValueWidget = (attr) => {
    const type = attr.input_type;
    const options = attr.options || [];
    if (['Dropdown', 'Visual Swatch', 'Text Swatch'].includes(type) && options.length > 0) {
      return 'dropdown';
    }
    if (type === 'Multiple Select' && options.length > 0) {
      return 'multiselect';
    }
    if (type === 'Text Field') {
      return 'text';
    }
    if (type === 'Yes/No') {
      return 'yesno';
    }
    return null;
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">
            {t('settings.system_attr_mapping')}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {t('settings.system_attr_mapping_desc')}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Database size={16} className="text-slate-400" />
            <select
              value={selectedStoreId}
              onChange={e => setSelectedStoreId(e.target.value)}
              className="rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm dark:text-slate-200 focus:ring-2 focus:ring-blue-500 min-w-[180px]"
            >
              <option value="">-- {t('common.select')} --</option>
              {stores.map(s => (
                <option key={s.auth_id} value={s.auth_id}>{s.store_name || s.auth_id}</option>
              ))}
            </select>
          </div>
          {selectedStoreId && canSave && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <Save size={16} className={saving ? 'animate-pulse' : ''} />
              {saving ? t('common.saving') : t('common.save')}
            </button>
          )}
        </div>
      </div>

      {/* No store selected */}
      {!selectedStoreId && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-8 text-center">
          <Database size={40} className="mx-auto text-slate-300 dark:text-slate-600 mb-3" />
          <p className="text-slate-500 dark:text-slate-400 text-sm">{t('settings.select_store_prompt')}</p>
        </div>
      )}

      {/* Loading state */}
      {selectedStoreId && loading && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-8 text-center">
          <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-3" />
          <p className="text-slate-500 dark:text-slate-400 text-sm">{t('common.loading')}</p>
        </div>
      )}

      {/* Mapping table */}
      {selectedStoreId && !loading && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          {/* Search */}
          <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder={t('common.search') + '...'}
              className="w-full max-w-xs rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm dark:text-slate-200 focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Table header */}
          <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-3 bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700 text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
            <div className="col-span-1 text-center">{t('common.update')}</div>
            <div className="col-span-3">{t('settings.store_attribute')} ({selectedStore?.store_name || selectedStoreId})</div>
            <div className="col-span-2 text-center">
              <Link size={14} className="inline" />
            </div>
            <div className="col-span-3">{t('settings.system_attribute')}</div>
            <div className="col-span-3">{t('settings.default_value')}</div>
          </div>

          {/* Rows */}
          {filteredStoreAttrs.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-sm">
              {searchTerm ? t('common.no_results') : t('common.no_data')}
            </div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-700">
              {filteredStoreAttrs.map(attr => {
                const chCode = attr.attribute_code;
                const mapping = getMapping(chCode);
                const isFormulaMode = mapping.erp_attribute_code === FORMULA_MODE;
                const defWidget = getDefaultValueWidget(attr);

                return (
                  <div key={chCode}
                    className="px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors"
                  >
                    {/* XS/SM: block layout */}
                    <div className="md:hidden space-y-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={!!checkedRows[chCode]}
                            onChange={() => toggleCheck(chCode)}
                            className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div>
                            <div className="font-mono text-sm font-medium text-slate-800 dark:text-slate-200">
                              {chCode}
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5">
                              {attr.default_label || '-'}
                              <span className="ml-2 inline-block px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 rounded text-[10px] text-slate-500 dark:text-slate-400">
                                {attr.input_type}
                              </span>
                            </div>
                          </div>
                        </div>
                        {mapping.erp_attribute_code && checkedRows[chCode] && (
                          <button
                            onClick={() => clearMapping(chCode)}
                            className="p-1 rounded text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                            title={t('common.remove')}
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>

                      {/* Source select */}
                      <div>
                        <select
                          value={mapping.erp_attribute_code || ''}
                          onChange={e => handleSourceChange(chCode, e.target.value)}
                          disabled={!checkedRows[chCode]}
                          className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm dark:text-slate-200 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <option value="">-- {t('common.not_set')} --</option>
                          <option value={FORMULA_MODE}>🧮 {t('settings.compute_formula')}</option>
                          <option disabled>──────────────</option>
                          {systemAttributes.map(sa => (
                            <option key={sa.attribute_code} value={sa.attribute_code}>
                              {sa.attribute_code} — {sa.default_label || sa.attribute_code}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Formula input */}
                      {isFormulaMode && (
                        <div>
                          <input
                            type="text"
                            value={mapping.formula}
                            onChange={e => handleFormulaChange(chCode, e.target.value)}
                            disabled={!checkedRows[chCode]}
                            placeholder="{cost} * 1.1 + {shipping}"
                            className="w-full rounded-lg border border-violet-300 dark:border-violet-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm font-mono text-violet-700 dark:text-violet-300 focus:ring-2 focus:ring-violet-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                          <div className="mt-2 text-xs text-amber-600 dark:text-amber-400 space-y-1">
                            <p>💡 {t('settings.formula_hint')}</p>
                            <p>💡 {'{currency_rate}'} = {t('settings.currency_rate_hint')}</p>
                            <p>💡 {'{profit_rate}'} = {t('settings.profit_rate_hint')}</p>
                          </div>
                        </div>
                      )}

                      {/* Default value */}
                      {defWidget === 'text' && (
                        <div>
                          <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">
                            {t('settings.default_value')}
                          </label>
                          <input
                            type="text"
                            value={mapping.default_value}
                            onChange={e => handleDefaultValueChange(chCode, e.target.value)}
                            disabled={!checkedRows[chCode]}
                            placeholder={t('settings.default_value_hint')}
                            className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm dark:text-slate-200 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                        </div>
                      )}
                      {defWidget === 'dropdown' && (
                        <div>
                          <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">
                            {t('settings.default_value')}
                          </label>
                          <select
                            value={mapping.default_value}
                            onChange={e => handleDefaultValueChange(chCode, e.target.value)}
                            disabled={!checkedRows[chCode]}
                            className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm dark:text-slate-200 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <option value="">-- {t('common.not_set')} --</option>
                            {attr.options.map(o => (
                              <option key={o.option_id} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      {defWidget === 'multiselect' && (
                        <div>
                          <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">
                            {t('settings.default_value')}
                          </label>
                          <select
                            multiple
                            value={mapping.default_value ? mapping.default_value.split(',').map(v => v.trim()) : []}
                            onChange={e => {
                              const selected = Array.from(e.target.selectedOptions, opt => opt.value);
                              handleDefaultValueChange(chCode, selected.join(','));
                            }}
                            disabled={!checkedRows[chCode]}
                            className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm dark:text-slate-200 focus:ring-2 focus:ring-blue-500 min-h-[100px] disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {attr.options.map(o => (
                              <option key={o.option_id} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      {defWidget === 'yesno' && (
                        <div>
                          <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">
                            {t('settings.default_value')}
                          </label>
                          <select
                            value={mapping.default_value}
                            onChange={e => handleDefaultValueChange(chCode, e.target.value)}
                            disabled={!checkedRows[chCode]}
                            className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm dark:text-slate-200 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <option value="">-- {t('common.not_set')} --</option>
                            <option value="0">No</option>
                            <option value="1">Yes</option>
                          </select>
                        </div>
                      )}
                    </div>

                    {/* MD+: grid layout */}
                    <div className="hidden md:grid grid-cols-12 gap-4 items-start">
                      {/* Checkbox */}
                      <div className="col-span-1 flex justify-center pt-3">
                        <input
                          type="checkbox"
                          checked={!!checkedRows[chCode]}
                          onChange={() => toggleCheck(chCode)}
                          className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                      </div>
                      {/* Left: store attribute info */}
                      <div className="col-span-3">
                        <div className="font-mono text-sm font-medium text-slate-800 dark:text-slate-200">
                          {chCode}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          {attr.default_label || '-'}
                          <span className="ml-2 inline-block px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 rounded text-[10px] text-slate-500 dark:text-slate-400">
                            {attr.input_type}
                          </span>
                        </div>
                      </div>

                      {/* Center: link arrow */}
                      <div className="flex col-span-2 justify-center pt-2">
                        <Link size={16} className="text-slate-300 dark:text-slate-600" />
                      </div>

                      {/* Right: source selection + formula */}
                      <div className="col-span-3 space-y-2">
                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            <select
                              value={mapping.erp_attribute_code || ''}
                              onChange={e => handleSourceChange(chCode, e.target.value)}
                              disabled={!checkedRows[chCode]}
                              className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm dark:text-slate-200 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <option value="">-- {t('common.not_set')} --</option>
                              <option value={FORMULA_MODE}>🧮 {t('settings.compute_formula')}</option>
                              <option disabled>──────────────</option>
                              {systemAttributes.map(sa => (
                                <option key={sa.attribute_code} value={sa.attribute_code}>
                                  {sa.attribute_code} — {sa.default_label || sa.attribute_code}
                                </option>
                              ))}
                            </select>
                          </div>
                          {mapping.erp_attribute_code && checkedRows[chCode] && (
                            <button
                              onClick={() => clearMapping(chCode)}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors shrink-0"
                              title={t('common.remove')}
                            >
                              <X size={16} />
                            </button>
                          )}
                        </div>

                        {/* Formula input */}
                        {isFormulaMode && (
                          <div>
                            <input
                              type="text"
                              value={mapping.formula}
                              onChange={e => handleFormulaChange(chCode, e.target.value)}
                              disabled={!checkedRows[chCode]}
                              placeholder="{cost} * 1.1 + {shipping}"
                              className="w-full rounded-lg border border-violet-300 dark:border-violet-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm font-mono text-violet-700 dark:text-violet-300 focus:ring-2 focus:ring-violet-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                            <div className="mt-2 text-xs text-amber-600 dark:text-amber-400 space-y-1">
                              <p>💡 {t('settings.formula_hint')}</p>
                              <p>💡 {'{currency_rate}'} = {t('settings.currency_rate_hint')}</p>
                              <p>💡 {'{profit_rate}'} = {t('settings.profit_rate_hint')}</p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Rightmost: default value */}
                      <div className="col-span-3 pt-0">
                        {defWidget === 'text' && (
                          <div>
                            <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">
                              {t('settings.default_value')}
                            </label>
                            <input
                              type="text"
                              value={mapping.default_value}
                              onChange={e => handleDefaultValueChange(chCode, e.target.value)}
                              disabled={!checkedRows[chCode]}
                              placeholder={t('settings.default_value_hint')}
                              className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm dark:text-slate-200 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                          </div>
                        )}
                        {defWidget === 'dropdown' && (
                          <div>
                            <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">
                              {t('settings.default_value')}
                            </label>
                            <select
                              value={mapping.default_value}
                              onChange={e => handleDefaultValueChange(chCode, e.target.value)}
                              disabled={!checkedRows[chCode]}
                              className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm dark:text-slate-200 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <option value="">-- {t('common.not_set')} --</option>
                              {attr.options.map(o => (
                                <option key={o.option_id} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                        {defWidget === 'multiselect' && (
                          <div>
                            <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">
                              {t('settings.default_value')}
                            </label>
                            <select
                              multiple
                              value={mapping.default_value ? mapping.default_value.split(',').map(v => v.trim()) : []}
                              onChange={e => {
                                const selected = Array.from(e.target.selectedOptions, opt => opt.value);
                                handleDefaultValueChange(chCode, selected.join(','));
                              }}
                              disabled={!checkedRows[chCode]}
                              className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm dark:text-slate-200 focus:ring-2 focus:ring-blue-500 min-h-[100px] disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {attr.options.map(o => (
                                <option key={o.option_id} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                        {defWidget === 'yesno' && (
                          <div>
                            <label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">
                              {t('settings.default_value')}
                            </label>
                            <select
                              value={mapping.default_value}
                              onChange={e => handleDefaultValueChange(chCode, e.target.value)}
                              disabled={!checkedRows[chCode]}
                              className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm dark:text-slate-200 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <option value="">-- {t('common.not_set')} --</option>
                              <option value="0">No</option>
                              <option value="1">Yes</option>
                            </select>
                          </div>
                        )}
                        {defWidget === null && (
                          <span className="text-xs text-slate-400 dark:text-slate-500 italic">
                            {t('settings.default_value_na')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Footer summary */}
          {filteredStoreAttrs.length > 0 && (
            <div className="px-4 py-3 bg-slate-50 dark:bg-slate-700/50 border-t border-slate-200 dark:border-slate-700 text-xs text-slate-500 dark:text-slate-400">
              {t('settings.total_store_attrs', { count: storeAttributes.length })}
              {' · '}
              {t('settings.mapped_attrs', { count: Object.keys(mappings).length })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SystemAttributeMappingPage;
